import React, { useState, useEffect } from 'react';
import { decodeFile } from '@/lib/qr/scanner';
import { DecodedQRResult } from '@/types/qr';
import { UploadCloud, Sparkles } from 'lucide-react';
import { useToast } from '@/components/ui/Toast';

interface ImageDropzoneScannerProps {
  onScanResult: (result: DecodedQRResult) => void;
}

export const ImageDropzoneScanner: React.FC<ImageDropzoneScannerProps> = ({ onScanResult }) => {
  const { showToast } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const processFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      showToast('error', 'Invalid File Type', 'Please upload a valid image file (PNG, JPG, WebP, SVG).');
      return;
    }

    try {
      setIsProcessing(true);
      const result = await decodeFile(file);
      setIsProcessing(false);

      if (result) {
        onScanResult(result);
      } else {
        showToast('warning', 'No QR Code Found', 'Could not detect or decode a QR code in this image.');
      }
    } catch (err) {
      setIsProcessing(false);
      console.error(err);
      showToast('error', 'Decoding Failed', 'Error while parsing the image file.');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile();
          if (file) {
            processFile(file);
            break;
          }
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      className={`relative w-full aspect-square max-w-md mx-auto rounded-2xl border border-dashed transition-all flex flex-col items-center justify-center p-8 text-center cursor-pointer ${
        isDragging
          ? 'border-primary bg-primary/5 scale-[1.01]'
          : 'border-border bg-card hover:bg-muted'
      }`}
    >
      <input
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) processFile(file);
        }}
        className="absolute inset-0 opacity-0 cursor-pointer"
      />

      <div className="p-4 rounded-full bg-muted text-primary mb-3">
        {isProcessing ? (
          <Sparkles className="h-8 w-8 animate-spin" />
        ) : (
          <UploadCloud className="h-8 w-8" />
        )}
      </div>

      <h4 className="text-[17px] font-semibold text-foreground tracking-tight">
        {isProcessing ? 'Analyzing QR Code...' : 'Drop QR Image or Browse'}
      </h4>
      <p className="text-[14px] text-muted-foreground mt-1 max-w-xs leading-relaxed">
        Supports PNG, JPG, WebP, or SVG. You can also paste an image from your clipboard (<kbd className="font-mono bg-muted px-1 py-0.5 rounded text-[11px]">⌘V</kbd>).
      </p>
    </div>
  );
};
